﻿import { ClientEnvInfo } from '../main/ClientEnvInfo.js';

class VersionHelper {
  // Note, is probably never used because it's only called with very old browsers
  /** Extract the numeric part of the version string. */
  static extractVersionNumber(versionString) {
    const numericString = versionString.match(/([0-9]+)\.([0-9]+)/i);
    return numericString.slice(1);
  }

  // Note, is probably never used because it's only called with very old browsers
  /** Compare 2 version strings by converting them to numeric format. */
  static compareVersions(version1, version2) {
    if (typeof version1 === 'undefined')
      if (typeof version2 === 'undefined')
        return 0;
      else
        return -1;
    else
      if (typeof version2 === 'undefined')
        return 1;
    for (let i = 0; i < version1.length; ++i) {
      const number1 = new Number(version1[i]);
      const number2 = new Number(version2[i]);
      if (number1 < number2)
        return -1;
      if (number1 > number2)
        return 1;
    }
    return 0;
  }

  // Note, is probably never used because it's only called with very old browsers
  /** Retrieve the version from the user agent string and compare with specified version. */
  static hasRuntimeVersion(userAgent, versionToCheck) {
    /* The test below only catches .NET 4.0. .NET 3.5 and below use a
       different pattern as the sample strings below show:

    .NET CLR 2.0.50727
    .NET CLR 3.0.30729
    .NET CLR 3.5.30729
    .NET4.0C
    .NET4.0E

    */
    const userAgentString = userAgent.match(/.NET[0-9.]+/g);
    if (typeof userAgentString !== 'undefined' && userAgentString !== null) {
      versionToCheck = this.extractVersionNumber(versionToCheck);
      for (let i = 0; i < userAgentString.length; ++i) {
        const agent = userAgentString[i].substr('.NET'.length);
        if (this.compareVersions(versionToCheck, this.extractVersionNumber(agent)) <= 0)
          return true;
      }
    }
    return false;
  }
}

class AsyncImage {
  constructor(url) {
    this.url = url;
  }

  /** Returns a Promise with an Image object. Can be called like this: const img = await asyncImage.load() */
  load() {
    return new Promise((resolve, reject) => {
      this.img = new Image();
      this.img.onload = () => resolve(img);
      this.img.onerror = () => reject(new Error(`Failed to load image at '${this.url}'`));
      this.img.src = this.url;
    });
  }
}

export class BrowserEnvironment {
  constructor() {
    this.dotNETRuntimeVersion35SP1 = '3.5.30729.00'; // .NET 3.5 SP1:
    this.dotNetRuntimeVersion40 = '4.0'; // .NET 4.0:
    this.device = null;
    this.isClientDeviceAnalyzed = false;
  }

  // Note, is currently not used because we have disabled the code which detects Chrome extensions in the analyzeAsync() method.
  /** Detects Chrome extension by its extension ID and trying to load a resource from it. */
  static async getChromeExtensionPresenceAsync(extensionId, detectionImagePath) {
    const img = new AsyncImage(`chrome-extension://${extensionId}/${detectionImagePath}`);
    try {
      await img.load();
      return true;
    } catch (ex) {
      return false;
    }
  }

  // Note, is currently not used because we have disabled the code which detects Chrome extensions in the analyzeAsync() method.
  static async detectMeta4ClickOnceLauncherPresenceAsync() {
    return await this.getChromeExtensionPresenceAsync('jkncabbipkgbconhaajbapbhokpbgkdc', 'images/download.png');
  }

  // Note, is currently not used because we have disabled the code which detects Chrome extensions in the analyzeAsync() method.
  static async detectClickOnceForChromePresenceAsync() {
    return await this.getChromeExtensionPresenceAsync('kekahkplibinaibelipdcikofmedafmb', 'blank.gif');
  }

  /* Note, this method might be called more than once, but because we use the this.isClientDeviceAnalyzed
   * property, there's no overhead in doing that. */
  analyzeClientDevice() {
    if (!this.isClientDeviceAnalyzed) {
      const envInfo = new ClientEnvInfo(/*autoRefresh:*/ true);
      this.device = envInfo.device;
      this.isClientDeviceAnalyzed = true;
    }
    return this.device;
  }

  /** Note, the results from this method affect what happens in the DesignerBtnManager.updateUIFromTestResults() method. */
  async analyzeAsync() {
    let oSRequirementOk = false;
    let runtimeRequirementOk = false;
    let isIE = false;
    let isEdge = false;
    let isGoogleChrome = false;
    let isFirefox = false;
    let browserSupportsClickOnce = false;
    const device = this.analyzeClientDevice();
    // Note, the results from this method affect what happens in the DesignerBtnManager.updateUIFromTestResults() method
    if (device.opSys === 'Windows') {
      oSRequirementOk = device.osVersion >= 5.1;
      isGoogleChrome = device.browserCategory === 'Chrome';
      isFirefox = device.browserCategory === 'Firefox';
      isEdge = device.browserCategory === 'Edge';
      if (isGoogleChrome) {
        // We have no way to find out if .NET runtime is installed; we assume it is...
        runtimeRequirementOk = true;
        let tryToUseChromeExtensions = false;
        if (tryToUseChromeExtensions) {
          browserSupportsClickOnce =
            await BrowserEnvironment.detectMeta4ClickOnceLauncherPresenceAsync() ||
            await BrowserEnvironment.detectClickOnceForChromePresenceAsync();
        }
      } else if (isFirefox) { // We have no way to find out if .NET runtime is installed; we assume it is...
        runtimeRequirementOk = true;
      } else if (isEdge && device.browserVersion >= 45) { // ClickOnce is no longer supported in Edge after version 45
        runtimeRequirementOk = true;
      } else if (isEdge && device.browserVersion >= 12) {
        browserSupportsClickOnce = true;
        runtimeRequirementOk = true;
      } else { // Old Edge, or other unknown browser, e.g. Explorer
        if (device.browserCategory === 'Explorer' && device.browserVersion >= 6) {
          isIE = true;
          browserSupportsClickOnce = true;
        }
        if (VersionHelper.hasRuntimeVersion(navigator.userAgent, this.dotNetRuntimeVersion40)) {
          runtimeRequirementOk = true;
        }
      }
    }
    const browserRequirementOk = (isIE || isGoogleChrome || isFirefox || isEdge) && browserSupportsClickOnce;
    const allRequirementsMet = oSRequirementOk && browserRequirementOk && runtimeRequirementOk;
    return {
      allRequirementsMet: allRequirementsMet,
      oSRequirementOk: oSRequirementOk,
      runtimeRequirementOk: runtimeRequirementOk,
      browserRequirementOk: browserRequirementOk,
      isIE: isIE,
      isGoogleChrome: isGoogleChrome,
      isFirefox: isFirefox,
      isEdge: isEdge,
      isClickOncePresent: browserSupportsClickOnce
    };
  }
}
